/** @format */

import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Card, Col, Collapse, Grid, Menu, Row, Spin } from "antd";
import { Select } from "antd";
import axios from "axios";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import type { Article, Articles } from "../../interfaces/articles";
import responsiveColumn from "../../methods/responsiveColumn";
import CustomBreadcrumb from "../components/CustomBreadcrumb";
import Footer from "../components/Footer";
import { getStaticProps } from "../../methods/i18n";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import SEO from "../components/seo";
import { HeadScriptTag } from "../components/HeadScriptTag";

export { getStaticProps };

export default function Faqs() {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined
  );

  const handleSelectChange = (value: string) => {
    setSelectedValue(value);
    setCatKey(value);
  };

  const [faqs, setFaqs] = useState<Articles>();
  const [query, setQuery] = useState<string>();
  const [filteredFAQS, setFilteredFAQS] = useState<Articles>();
  const [catKey, setCatKey] = useState<string | number>(14);

  const router = useRouter();
  const { locale } = router;

  const { useBreakpoint } = Grid;
  const screenSize = useBreakpoint();
  const { Option } = Select;

  const setQueryParam = useMemo(() => {
    if (locale !== "en") {
      return `?lang=${locale}&`;
    } else {
      return "?";
    }
  }, [locale]);

  interface MenuType {
    key: string;
    label: string;
  }

  const { t } = useTranslation(["faqs", "common", "breadcrumb_section", "meta_tags"]);

  useEffect(() => {
    if (catKey)
      setFilteredFAQS(
        faqs?.filter((r) => r.categories.includes(parseInt(catKey as string)))
      );
  }, [faqs, catKey]);

  const fetchFAQS = async () => {
    try {
      const r = await axios({
        url: `https://wp.taxworkoutgroup.com/wp-json/wp/v2/faqs${setQueryParam}_embed&per_page=100`,
        method: "get",
      });
      setFaqs(
        r.data.sort((a: Article, b: Article) => a.acf.priority - b.acf.priority)
      );
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchFAQS();
  }, [locale]);

  const menuItems: MenuType[] = [
    {
      label: t("common:tax_bankruptcy_group"),
      key: "14",
    },
    {
      label: t("common:tax_controversy_group"),
      key: "17",
    },
    {
      label: t("common:tax_compliance_group"),
      key: "22",
    },
    {
      label: t("common:criminal_tax_defense_group"),
      key: "23",
    },
    {
      label: t("common:florida_practice_group"),
      key: "93",
    },
    {
      label: t("common:estate_and_trust_planning"),
      key: "99",
    },
  ];

  const title = t("meta_tags:faqs");
  const description = t("meta_tags:faqs_desc")
  const keywords = "Tax,Tax Bankruptcy ,Tax Controversy, FAQs";
  // const ogImage = "https://www.example.com/static/images/og-image.jpg";
  const ogType = "article";
  const canonicalURL = `https://taxworkoutgroup.com/faqs`;

  return (
    <>
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        // ogImage={ogImage}
        ogType={ogType}
        canonicalURL={canonicalURL}
      />
      <HeadScriptTag />
      <CustomBreadcrumb
        items={[
          {
            label: t("breadcrumb_section:home"),
            href: "/",
          },
          {
            label: t("breadcrumb_section:faqs"),
            href: "/faqs",
          },
        ]}
      />
      <Row justify={"center"}>
        <Col
          span={24}
          style={{
            backgroundImage: `url(/twg_faqs.jpeg)`,
            height: screenSize.md ? 470 : 570,
            backgroundSize: "cover",
            backgroundPosition: screenSize.md
              ? "center center"
              : "-320px center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              padding: screenSize.md ? "0 10% 0 50%" : "450px 5% 70px 5%",
            }}
          >
            <div className={"section_title"}>
              <h1
                style={{
                  color: "#fff",
                  fontSize: screenSize.md ? 34 : 24,
                  marginBottom: 30,
                }}
              >
                {t("faqs:faqs_title")}
              </h1>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "none",
                padding: "5px 10px",
                fontSize: 16,
                width: "100%",
                color: "rgba(255,255,255,0.6)",
                display: "inline-flex",
              }}
            >
              <SearchOutlined />
              <input
                id="faq_search"
                onChange={(e) => setQuery(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    router.push({
                      pathname: "/resources/search",
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      query: { q: e.nativeEvent.target.value },
                    });
                  }
                }}
                style={{
                  outlineColor: "transparent",
                  background: "transparent",
                  border: "none",
                  padding: 10,
                  fontSize: 16,
                  width: "100%",
                }}
                placeholder={t("faqs:faqs_search") ?? ""}
              />
            </div>
          </div>
        </Col>
      </Row>
      <Row justify={"center"} style={{ paddingTop: 50, paddingBottom: 50 }}>
        <Col {...responsiveColumn([18, 18, 18, 22, 22, 22])}>
          <Card size="small" hoverable>
            {screenSize?.sm ? (
              <Menu
                className="faq_menu"
                items={menuItems}
                defaultActiveFirst
                onSelect={(e) => setCatKey(e.key)}
                mode="horizontal"
                style={{
                  justifyContent: "space-around",
                  border: "none",
                }}
              />
            ) : (
              <Select
                className="my-custom-select"
                style={{
                  width: "300px",
                  textAlign: "center",
                  border: "none",
                }}
                bordered={false}
                defaultValue={t("common:tax_bankruptcy_group")}
                onChange={handleSelectChange}
              >
                {menuItems.map((option) => (
                  <Option
                    key={option.label}
                    value={option.key}
                    className="my-custom-option"
                  >
                    {option.label}
                  </Option>
                ))}
              </Select>
            )}
          </Card>
          <Spin spinning={!faqs ? true : false}>
            <Collapse
              expandIconPosition="right"
              style={{ marginTop: 50 }}
              ghost
              expandIcon={({ isActive }) =>
                isActive ? (
                  <MinusCircleOutlined
                    style={{
                      fontSize: 20,
                    }}
                  />
                ) : (
                  <PlusCircleOutlined
                    style={{
                      fontSize: 20,
                    }}
                  />
                )
              }
            >
              {(
                (query
                  ? faqs?.filter((e) =>
                      JSON.stringify(e)
                        .toLowerCase()
                        .includes(query.toLowerCase())
                    )
                  : filteredFAQS) ?? []
              )
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((e: any) => {
                  return (
                    <Collapse.Panel
                      key={e.id}
                      header={
                        <span style={{ fontSize: 16, fontWeight: 500 }}>
                          {e.title.rendered}
                        </span>
                      }
                    >
                      <p
                        className="wp"
                        dangerouslySetInnerHTML={{
                          __html: e.content.rendered ?? "",
                        }}
                        style={{
                          overflow: "hidden",
                          width: "100%",
                          lineHeight: 2,
                          fontSize: 16,
                        }}
                      ></p>
                    </Collapse.Panel>
                  );
                })}
            </Collapse>
          </Spin>
        </Col>
      </Row>
      <Row justify={"center"} align={"middle"}>
        <Col
          {...responsiveColumn([18, 18, 18, 22, 22, 22])}
          style={{ textAlign: "center", marginBottom: 60 }}
        >
          <p style={{ fontSize: 20 }}>
            <b>{t("faqs:faqs_p1")}</b>
          </p>
          <Link href="/contact-us">
            <span
              style={{
                color: "#254486",
                textDecoration: "underline",
                fontSize: 16,
              }}
            >
              {t("faqs:faqs_button")}
            </span>
          </Link>
        </Col>
      </Row>
      <p className="invisible-number">(866) 845-7080</p>

      <Footer />
    </>
  );
}
